var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { URLData } from '@telia-common-components/shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { debounce } from 'throttle-debounce';
import API from '../../../util/ApiUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import { FetchStatus } from '../../../util/FetchStatus';
class AddressAutocompleteStore {
    constructor(tier, app, lang, address, room, onSubmit) {
        this.addressText = '';
        this.roomText = '';
        this.roomTextInMemory = '';
        this.isApartmentInvalid = false;
        this.error = null;
        this.status = FetchStatus.UNINITIALIZED;
        this.addressAutocompleteItems = [];
        this.roomSuggestionsItems = [];
        this.allRoomOptions = [];
        this.onAddressAutocompleteChange = (searchTerm) => {
            if (this.addressText === searchTerm) {
                return;
            }
            this.addressText = searchTerm;
            this.selectedAddress = null;
            this.allRoomOptions = [];
            this.roomSuggestionsItems = [];
            this.roomText = '';
            if (!this.findSuggestionItemByLabel(this.suggestions, this.addressText)) {
                this.setStatus(FetchStatus.FETCHING);
                this.debouncedFetchAddressAutocompleteOptions();
            }
        };
        this.onAddressAutocompleteSelectAddress = async (item) => {
            this.selectedAddressItem = item;
            return this.onAddressAutocompleteSelection(true);
        };
        this.clearAddress = () => {
            this.addressAutocompleteItems = [];
            this.addressText = '';
            this.selectedAddress = null;
            this.allRoomOptions = [];
            this.roomSuggestionsItems = [];
            this.error = '';
            this.isApartmentInvalid = false;
        };
        this.onAddressAutocompleteRoomFocus = () => {
            this.isApartmentInvalid = false;
            this.roomSuggestionsItems = this.allRoomOptions;
            this.roomTextInMemory = this.roomText;
            this.roomText = '';
        };
        this.onAddressAutocompleteRoomBlur = () => {
            if (!this.roomText) {
                this.roomText = this.roomTextInMemory;
            }
            this.roomTextInMemory = '';
        };
        this.onRoomAutocompleteChange = (value) => {
            this.roomText = value;
            this.clearError();
            if (!value) {
                this.roomSuggestionsItems = this.allRoomOptions;
                return;
            }
            const filteredRooms = this.allRoomOptions.filter((room) => room.label.startsWith(value, 0) && room.label.toLowerCase().includes(value.toLocaleLowerCase()));
            if (filteredRooms.length) {
                this.roomSuggestionsItems = filteredRooms;
            }
        };
        this.clearError = () => {
            this.error = null;
        };
        this.onAddressAutocompleteSelection = async (isSelect = false) => {
            var _a;
            if (!isSelect) {
                this.selectedAddressItem = this.findSuggestionItemByLabel(this.suggestions, this.addressText);
            }
            this.allRoomOptions = [];
            this.roomText = '';
            if (this.selectedAddressItem) {
                const [addressId, buildingId] = this.selectedAddressItem.id.split('-');
                this.selectedAddress = await this.getAddressById(addressId, buildingId);
                if ((_a = this.selectedAddress.rooms) === null || _a === void 0 ? void 0 : _a.length) {
                    this.allRoomOptions = this.sortAndMapRoomsToSuggestionItems(this.selectedAddress.rooms);
                    this.roomSuggestionsItems = this.allRoomOptions;
                    this.focusRoomField();
                }
            }
        };
        this.findRoom = () => {
            const roomOption = this.findSuggestionItemByLabel(this.allRoomOptions, this.roomText);
            return roomOption ? { roomId: roomOption.id, roomName: roomOption.label } : { roomName: this.roomText };
        };
        this.findSuggestionItemByLabel = (suggestions, label) => {
            return suggestions.find((item) => item.label === label);
        };
        this.sortAndMapRoomsToSuggestionItems = (rooms) => {
            return rooms
                .map((room) => ({
                label: room.roomName,
                id: room.roomId,
                onClick: this.clearError,
                url: '#'
            }))
                .sort((a, b) => parseInt(a.label, 10) - parseInt(b.label, 10));
        };
        this.focusRoomField = () => {
            setTimeout(() => { var _a; return (_a = document.getElementById('addressAutocomplete-apartment-field')) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
        };
        makeObservable(this);
        this.urlData = new URLData({ app: app, tier: tier, lang: lang });
        this.debouncedFetchAddressAutocompleteOptions = debounce(250, this.fetchAddressAutocompleteOptions);
        this.addressApi = API(this.urlData.commonAPIBaseURL, { withCredentials: true });
        this.init(address, room, onSubmit);
    }
    async init(address, room, onSubmit) {
        if (address) {
            address = await this.getAddressById(address.id, address.buildingId);
            this.selectedAddress = address;
            this.addressText = address.label;
            this.addressAutocompleteItems = [
                {
                    id: `${address.id}-${address.buildingId}`,
                    url: '#',
                    label: address.label,
                    isActive: true,
                    onClick: this.clearError
                }
            ];
            this.selectedAddressItem = this.addressAutocompleteItems[0];
            this.allRoomOptions = this.sortAndMapRoomsToSuggestionItems(address.rooms || []);
            this.roomSuggestionsItems = this.allRoomOptions;
            if (room) {
                this.roomText = room.roomName;
                this.roomSuggestionsItems = [
                    {
                        label: room.roomName,
                        id: room.roomId,
                        onClick: this.clearError,
                        url: '#'
                    }
                ];
            }
            if (!room && this.allRoomOptions.length) {
                this.focusRoomField();
            }
            else {
                onSubmit(address, room);
            }
        }
    }
    fetchAddressResult(queryString) {
        if (!queryString) {
            return Promise.resolve([]);
        }
        return this.addressApi
            .get('/address/suggest', {
            params: { term: queryString },
            timeout: 30000
        })
            .then((response) => response.data)
            .catch((e) => {
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
            return [];
        });
    }
    getAddressById(addressId, buildingId) {
        return this.addressApi
            .get(`/address/${addressId}/${buildingId}`, { timeout: 30000 })
            .then((result) => result.data)
            .catch((e) => {
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
            return null;
        });
    }
    get isFetchingResults() {
        return this.status === FetchStatus.FETCHING || (this.addressText && this.status === FetchStatus.UNINITIALIZED);
    }
    get suggestions() {
        return this.addressAutocompleteItems.slice();
    }
    get roomSuggestions() {
        return this.roomSuggestionsItems.slice();
    }
    async fetchAddressAutocompleteOptions() {
        this.clearError();
        try {
            const addresses = await this.fetchAddressResult(this.addressText);
            const addressLabels = addresses.map((address) => address.label);
            const duplicateAddressLabels = addressLabels.filter((label, index) => addressLabels.indexOf(label) !== index);
            this.addressAutocompleteItems = addresses.map((address) => ({
                id: `${address.id}-${address.buildingId}`,
                url: '#',
                label: address.label,
                subLabel: duplicateAddressLabels.includes(address.label) ? address.buildingId : undefined,
                isActive: address.label === this.addressText,
                onClick: this.clearError
            }));
            this.setStatus(FetchStatus.FETCHED);
            this.findSuggestionItemByLabel(this.suggestions, this.addressText) && this.onAddressAutocompleteSelection();
        }
        catch (e) {
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
            this.setStatus(FetchStatus.ERROR);
        }
    }
    setStatus(newStatus) {
        this.status = newStatus;
    }
    isValidApartment() {
        return !this.allRoomOptions.length || !!this.findRoom().roomId;
    }
    handleApartmentNotice() {
        this.isApartmentInvalid = !this.isValidApartment();
        if (this.isApartmentInvalid) {
            this.error = 'mandatoryRoom';
        }
    }
}
__decorate([
    observable
], AddressAutocompleteStore.prototype, "selectedAddress", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "addressText", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "roomText", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "roomTextInMemory", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "isApartmentInvalid", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "error", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "status", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "urlData", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "addressAutocompleteItems", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "roomSuggestionsItems", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "allRoomOptions", void 0);
__decorate([
    observable
], AddressAutocompleteStore.prototype, "selectedAddressItem", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "init", null);
__decorate([
    action
], AddressAutocompleteStore.prototype, "fetchAddressResult", null);
__decorate([
    action
], AddressAutocompleteStore.prototype, "getAddressById", null);
__decorate([
    computed
], AddressAutocompleteStore.prototype, "isFetchingResults", null);
__decorate([
    computed
], AddressAutocompleteStore.prototype, "suggestions", null);
__decorate([
    computed
], AddressAutocompleteStore.prototype, "roomSuggestions", null);
__decorate([
    action
], AddressAutocompleteStore.prototype, "onAddressAutocompleteChange", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "onAddressAutocompleteSelectAddress", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "clearAddress", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "onAddressAutocompleteRoomFocus", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "onAddressAutocompleteRoomBlur", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "onRoomAutocompleteChange", void 0);
__decorate([
    action
], AddressAutocompleteStore.prototype, "clearError", void 0);
__decorate([
    action.bound
], AddressAutocompleteStore.prototype, "fetchAddressAutocompleteOptions", null);
__decorate([
    action
], AddressAutocompleteStore.prototype, "onAddressAutocompleteSelection", void 0);
__decorate([
    action.bound
], AddressAutocompleteStore.prototype, "setStatus", null);
__decorate([
    action
], AddressAutocompleteStore.prototype, "handleApartmentNotice", null);
export default AddressAutocompleteStore;
